import * as React from 'react';
import * as acl from 'common/utils/acl';

import { EntityActions, EntityType } from 'common/entity';

import { AppState } from 'common/models/AppState';
import { Badge } from 'common/components/Badge';
import { Datetime } from 'common/components/Datetime';
import { InfiniteList } from 'common/components/InfiniteList';
import { Loading } from 'common/components/Loading';
import { LinkedinLink } from 'common/components/link/LinkedinLink';
import { MemberLink } from 'common/components/link/MemberLink';
import { Table } from 'reactstrap';
import { toApiError } from 'common/utils/api';
import { MemberApi } from 'common/api/resources/MemberApi';
import { Member } from 'common/api/models/Member';
import { CONTACT_TYPES } from '../../common/constants';

interface UsersTableProps {
  appState: AppState;
  entityActions: EntityActions;
  search: string;
  archivedMode?: boolean;
  onClick: (num: string) => void;
  onEdit: (user: Member) => void;
}
interface UsersTableState {
  users: Member[];
  next?: string;
  isLoading: boolean;
  error?: string;
}
export class UsersTable extends React.Component<UsersTableProps, UsersTableState> {
  state: UsersTableState = { users: [], isLoading: true };

  handleRowClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
    e.stopPropagation();
    this.props.onClick(e.currentTarget.dataset.number!);
  };

  handleLoadMore = () => {
    this.reload(this.props.search, this.state.next);
  };

  componentDidMount() {
    this.reload(this.props.search);
  }

  componentDidUpdate(prevProps: UsersTableProps) {
    if (prevProps.search !== this.props.search) {
      this.reload(this.props.search);
    }
  }

  reload(searchQuery: string, from?: string) {
    this.setState(prevState => ({
      ...prevState,
      isLoading: true,
      ...(from ? {} : { users: [], next: undefined }),
    }));
    const archivedToken = this.props.archivedMode ? ' archived:1' : '';
    MemberApi.findMembers({ q: searchQuery + ' type:user' + archivedToken, from: from }).then(
      results => {
        this.setState(prevState => ({
          ...prevState,
          users: from ? prevState.users.concat(results.data!) : results.data!,
          next: results.next,
          error: undefined,
          isLoading: false,
        }));
        let orgIds: string[] = [];
        for (let u of results.data!) {
          if (u.orgs) {
            for (let o of u.orgs) {
              orgIds.push(o.memberId!);
            }
          }
        }
        this.props.entityActions.fetchMissingIds(EntityType.MEMBER, orgIds);
      },
      err => {
        this.setState({ error: toApiError(err), isLoading: false });
      }
    );
  }

  render() {
    const cols = 7;
    const entities = this.props.appState.entities;
    return (
      <InfiniteList hasMore={!!this.state.next} isLoading={this.state.isLoading} onLoadMore={this.handleLoadMore}>
        {this.state.error && (
          <div className="alert alert-danger" style={{ marginTop: 20 }}>
            {this.state.error}
          </div>
        )}

        <Table className="table-clickable" responsive={true} hover={true} striped={true}>
          <thead>
            <tr>
              <th>Name</th>

              <th>Organization</th>
              <th>Chapter</th>
              <th>Last Login</th>
              <th>Status</th>
              <th>Expire</th>
              <th>Access</th>
              <th>Job Title</th>
              <th>LinkedIn Page</th>
            </tr>
          </thead>
          <tbody>
            {!this.state.isLoading &&
              !this.state.error &&
              this.state.users.length === 0 && (
                <tr>
                  <td colSpan={cols}>No results found.</td>
                </tr>
              )}
            {this.state.users.map(u => (
              <tr key={u.id} data-number={u.number} onClick={this.handleRowClick}>
                <td>
                  {u.firstName} {u.lastName}
                </td>
                <td>
                  {u.orgs &&
                    u.orgs.map(o => (
                      <div key={u.id + o.memberId}>
                        <MemberLink member={this.props.appState.entities.member[o.memberId]} />{' '}
                        {entities.member[o.memberId] &&
                          entities.member[o.memberId].contacts &&
                          CONTACT_TYPES.filter(c => entities.member[o.memberId].contacts!![c.value] === u.id).map(c => (
                            <span
                              key={`${o.memberId}-${c.value}`}
                              className="badge badge-pill"
                              style={{ backgroundColor: c.color, marginRight: 5 }}
                            >
                              {c.label}
                            </span>
                          ))}
                      </div>
                    ))}
                </td>
                <td>
                  {u.chapterId && entities.chapter[u.chapterId] && entities.chapter[u.chapterId].name}
                  {!u.chapterId && 'None'}
                </td>
                <td>
                  <Datetime at={u.loginAt} />
                </td>
                <td>
                  {u.status === Member.Status.ACTIVE && <Badge type="success" text="Active" />}{' '}
                  {u.status === Member.Status.EXPIRED && <Badge type="danger" text="Expired" />}{' '}
                  {u.status === Member.Status.INACTIVE && <Badge type="danger" text="Inactive" />}{' '}
                </td>
                <td>
                  <Datetime at={u.expireDate} format="M/D/YY" />
                </td>
                <td>
                  {u.adminAccess !== Member.AdminAccess.NONE && (
                    <Badge type="primary" text={acl.toAdminAccessLabel(u.adminAccess!)} />
                  )}
                </td>
                <td>{u.jobTitle || 'None'}</td>
                <td>
                  <LinkedinLink member={u} />
                </td>
              </tr>
            ))}
            {this.state.isLoading && (
              <tr>
                <td colSpan={cols}>
                  <Loading />
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </InfiniteList>
    );
  }
}



// WEBPACK FOOTER //
// ./src/screens/Users/UsersTable.tsx